import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/openplay/BottomTabNavigation';
import Icon from '../../components/openplay/OpenPlayIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';

const pad2 = (n) => String(n).padStart(2, '0');

const toDateString = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const storagePathFromUrl = (url) => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    const token = '/storage/v1/object/public/session-images/';
    const idx = parsed.pathname.indexOf(token);
    if (idx === -1) return null;
    return decodeURIComponent(parsed.pathname.slice(idx + token.length));
  } catch {
    return null;
  }
};

const CodeManagementPage = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const loadCodes = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data, error } = await supabase
        .from('pickleball_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        setLoadError(error?.message || '코드 목록을 불러오지 못했습니다.');
        setCodes([]);
        return;
      }
      setCodes(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const handleDelete = async (codeId) => {
    if (!codeId) return;
    const confirmed = window.confirm('정말 삭제할까요?');
    if (!confirmed) return;
    const current = (codes || []).find((code) => code?.id === codeId);
    const urlsToRemove = [
      ...(Array.isArray(current?.gallery_urls) ? current.gallery_urls : []),
      current?.cover_image_url,
    ]
      .map((u) => String(u || '').trim())
      .filter(Boolean);
    const { error } = await supabase.from('pickleball_codes').delete().eq('id', codeId);
    if (error) {
      alert(error?.message || '삭제에 실패했습니다.');
      return;
    }
    const storagePaths = urlsToRemove.map(storagePathFromUrl).filter(Boolean);
    if (storagePaths.length > 0) {
      await supabase.storage.from('session-images').remove(storagePaths);
    }
    await loadCodes();
  };

  const sortedCodes = useMemo(() => {
    return (codes || []).map((code) => ({
      ...code,
      createdLabel: toDateString(code?.created_at),
    }));
  }, [codes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortedCodes.length]);

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(sortedCodes.length / pageSize));
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const pagedCodes = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedCodes.slice(start, start + pageSize);
  }, [safePage, sortedCodes]);

  return (
    <div className="min-h-screen bg-background">
      <div className="md:pl-[240px]">
        <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-0">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">코트 관리</h1>
                <p className="text-base md:text-lg caption text-muted-foreground">
                  피클볼 코트 장소를 등록하고 관리하세요.
                </p>
              </div>
              <Button type="button" variant="default" onClick={() => navigate('/openplay/code-management/new')}>
                코드등록
              </Button>
            </div>

            {loadError && (
              <div className="mb-6 bg-error/10 border border-error/20 rounded-xl p-4">
                <p className="text-sm text-error">{loadError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6">
              <section className="space-y-4">
                {loading ? (
                  <div className="bg-card border border-border rounded-xl p-6 text-sm text-muted-foreground">로딩 중...</div>
                ) : sortedCodes.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-6 text-sm text-muted-foreground">등록된 코드가 없습니다.</div>
                ) : (
                  pagedCodes.map((code) => {
                    const cover = String(code?.cover_image_url || '').trim();
                    const gallery = Array.isArray(code?.gallery_urls) ? code.gallery_urls : [];
                    const preview = cover || String(gallery?.[0] || '').trim();
                    return (
                      <div key={code.id} className="bg-card border border-border rounded-xl p-5 flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-40 h-28 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                          {preview ? (
                            <img src={preview} alt={code?.name || 'code'} className="w-full h-full object-cover" />
                          ) : (
                            <Icon name="PhotoIcon" size={28} className="text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-lg font-semibold text-foreground truncate">{code?.name}</h3>
                            <span className="text-xs text-muted-foreground">{code?.createdLabel}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{code?.address || '주소 미등록'}</p>
                          <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
                            <span className="px-2 py-1 rounded-full bg-muted">{code?.indoor ? '실내' : '실외'}</span>
                            {code?.court_count != null && (
                              <span className="px-2 py-1 rounded-full bg-muted">코트 {code.court_count}면</span>
                            )}
                            {code?.floor_material && (
                              <span className="px-2 py-1 rounded-full bg-muted">{code.floor_material}</span>
                            )}
                            {Array.isArray(code?.price_info) && code.price_info.length > 0 && (
                              <span className="px-2 py-1 rounded-full bg-muted">가격 {code.price_info.length}개</span>
                            )}
                          </div>
                          <div className="mt-4 flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/openplay/code-management/${code.id}`)}>
                              수정
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(code.id)}>
                              삭제
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                {!loading && sortedCodes.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <p className="text-xs text-muted-foreground">
                      전체 {sortedCodes.length}개 · {safePage} / {totalPages} 페이지
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={safePage <= 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      >
                        이전
                      </Button>
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                        .slice(Math.max(0, safePage - 3), Math.max(0, safePage - 3) + 5)
                        .map((page) => (
                          <Button
                            key={`page-${page}`}
                            type="button"
                            variant={page === safePage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={safePage >= totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      >
                        다음
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </main>
      </div>

      <BottomTabNavigation userRole="player" />
    </div>
  );
};

export default CodeManagementPage;
